import { ITableQueryData } from "types/frontend_models/table/ITableQueryData";
import { Filters, SortingRule } from 'react-table';
import { ITableFilter, FilterOperators } from "types/frontend_models/filtering/filters";
import { IDateRange } from "types/frontend_models/time/IDateRange";
import { dateRangeToSearchString } from "functions/parsers/DateRangeParsers";

export const defaultDataTablePagesizeOptions = ['10', '15', '20', '25', '50'];
export const defaultDataTablePageSize = 10;

/** Returns a new table query object. All properties of the new data will override or combine the old one */
export function combineTableQueryData(
	queryDataNew: ITableQueryData,
	queryDataOld: ITableQueryData
): ITableQueryData
{
	let combined: ITableQueryData = {};
	combined.currentPage = (queryDataNew.currentPage) ? queryDataNew.currentPage : queryDataOld.currentPage;
	combined.pageSize = (queryDataNew.pageSize) ? queryDataNew.pageSize : queryDataOld.pageSize;
	combined.filters = (queryDataNew.filters) ? queryDataNew.filters : queryDataOld.filters;
	combined.sorted = (queryDataNew.sorted !== null && queryDataNew.sorted !== undefined) ? queryDataNew.sorted : queryDataOld.sorted;

	// console.log('combined', combined);

	return combined;
}

/** Defines which property use which filter operator in a data fetch request */
export interface IKeyAndOperator
{
	key: string,
	operator: FilterOperators
}

export function convertFiltersToTableFilters(
	filters: Filters<Object>,
	keysAndOperators: IKeyAndOperator[]
): ITableFilter[]
{
	const tableFilters: ITableFilter[] = [];

	filters.forEach(f =>
	{
		if (f.value === undefined) {
			return;
		} else if (typeof f.value === 'string') {
			const tableFilter = convertStringFilterToTableFilter(f.id, f.value, keysAndOperators);
			tableFilters.push(tableFilter);
		} else if ((f.value as IDateRange) !== null) {
			const rangeTableFilter = convertDateRangeToTableFilter(f.id, f.value as IDateRange);
			if (rangeTableFilter) tableFilters.push(rangeTableFilter);
		}
	});

	return tableFilters;
}

function convertStringFilterToTableFilter(id: string, value: string, keysAndOperators: IKeyAndOperator[]): ITableFilter
{
	let tableFilter: ITableFilter = { key: '', operator: FilterOperators.undefined, value: '' };
	tableFilter.key = id;
	const keyAndOperator = keysAndOperators.find(kao => kao.key === id);
	if (keyAndOperator) {
		tableFilter.operator = keyAndOperator.operator;
	} else {
		throw new Error(`Could not find operator by key ${id}. Please make sure it exists.`);
	}
	tableFilter.value = value;
	return tableFilter;
}

/**
 * Returns two table filters for the same property: date>start and date<end.
 */
function convertDateRangeToTableFilter(id: string, dateRange: IDateRange): ITableFilter | undefined
{
	// Only return a date range if we have a start or end date
	if (dateRange.startDate || dateRange.endDate) {
		let tableFilter: ITableFilter = {
			key: id,
			operator: FilterOperators.dateRange,
			value: dateRangeToSearchString(dateRange)
		}

		return tableFilter;
	} else {
		return undefined;
	}
}

export function getSorts(sortingRules: SortingRule<Object>[]): string
{
	if (sortingRules.length !== 1) return '';

	let sortedValue = sortingRules[0].id;
	if (sortingRules[0].desc) {
		sortedValue = '-' + sortedValue;
	}
	return sortedValue;
}

/** Returns initial filter value if found in passed filter collection. Use when expecting single result. */
export function findInitialFilter<T>(
	property: keyof T,
	initialFilters: ITableFilter[] | undefined
)
{
	if (initialFilters) {
		return initialFilters.find(f => f.key === property);
	} else {
		return undefined;
	}
}

/** Returns initial filter values if found in passed filter collection. 
 * Use when expecting multiple results. */
export function findInitialFilters<T>(
	property: keyof T,
	initialFilters: ITableFilter[] | undefined
)
{
	if (initialFilters) {
		return initialFilters.filter(f => f.key === property);
	} else {
		return undefined;
	}
}